import type { SdkRestResult } from "../../rest";
import type { LiveCallMessages } from "./platform";

export const LIVE_CALL_DISABLED_MESSAGE =
  "Voice calls aren't enabled for your account yet.";
export const LIVE_CALL_UNCONFIGURED_MESSAGE =
  "Voice calls aren't set up on this server yet.";
export const CALL_DROPPED_MESSAGE =
  "The voice call dropped. Check your connection.";

export const BROWSER_MESSAGES: LiveCallMessages = {
  micDenied:
    "Microphone access was denied. Allow it in the browser’s site settings.",
  signedOut: "You’re signed out. Refresh the page and sign in again.",
};

type MintFailure = Extract<SdkRestResult<unknown>, { ok: false }>;

export function sessionErrorMessage(
  failure: MintFailure,
  messages: LiveCallMessages
): string {
  const { status, code, retryAfterSeconds } = failure;
  if (status === 429) {
    const who =
      code === "UPSTREAM_RATE_LIMITED" ? "The voice provider is" : "You’re";
    return retryAfterSeconds
      ? `${who} rate limited. Try again in ${retryAfterSeconds}s.`
      : `${who} rate limited. Try again in a minute.`;
  }
  if (status === 401) {
    return messages.signedOut;
  }
  if (code === "CALL_LIMIT_REACHED") {
    return "This call reached its time limit. Start a new call to keep talking.";
  }
  if (status === 409) {
    return "You’re already on a call. Hang up before starting another.";
  }
  if (code === "UPSTREAM_ERROR") {
    return "The voice provider refused the call.";
  }
  return `Couldn’t start the voice call (HTTP ${status}).`;
}

// Not `instanceof Error`: react-native-webrtc rejects with plain objects.
function errorField(err: unknown, field: "name" | "message"): string {
  return typeof err === "object" && err !== null && field in err
    ? String((err as Record<typeof field, unknown>)[field])
    : "";
}

export function startErrorMessage(
  err: unknown,
  messages: LiveCallMessages
): string {
  const message = errorField(err, "message");
  switch (errorField(err, "name")) {
    case "NotAllowedError":
    case "SecurityError":
    case "PermissionDeniedError":
      return messages.micDenied;
    case "NotFoundError":
    case "OverconstrainedError":
      return "No microphone found.";
    case "NotReadableError":
      return "The microphone is in use by another app.";
    case "TypeError":
      return message.includes("fetch") ||
        message.includes("Network request failed")
        ? "Couldn’t reach the server. Check your connection."
        : message || "Couldn’t start the voice call.";
    default:
      return message || "Couldn’t start the voice call.";
  }
}
